import React, { useCallback, useEffect, useState } from 'react';
import { missionControlService } from '../../../services/missionControl.js';
import type { ProductConfig, ProductIntelItemConfig } from '../../../services/missionControl.js';
import './product-intel-settings.css';

const PLATFORMS = ['WEB', 'iOS', 'visionOS'] as const;

const PLATFORM_CSS: Record<string, string> = {
  WEB: 'web',
  iOS: 'ios',
  visionOS: 'visionos',
};

interface ProductIntelSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProductIntelSettings({
  isOpen,
  onClose,
}: ProductIntelSettingsProps): React.ReactElement | null {
  const [configs, setConfigs] = useState<ProductConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await missionControlService.getProductConfigs();
      if (result.success && result.data) {
        setConfigs(result.data);
      } else {
        setError('Failed to load product configurations.');
      }
    } catch {
      setError('Failed to load product configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      void fetchConfigs();
    }
  }, [isOpen, fetchConfigs]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  async function handleUpdateProduct(
    id: string,
    field: 'name' | 'platform',
    value: string,
    oldValue: string
  ) {
    setUpdatingIds((prev) => new Set(prev).add(id));
    // Optimistic update
    setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
    try {
      const result = await missionControlService.updateProductConfig(id, { [field]: value });
      if (!result.success) {
        setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: oldValue } : c)));
      }
    } catch {
      setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: oldValue } : c)));
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  async function handleUpdateItem(
    itemId: string,
    productId: string,
    field: 'label' | 'value',
    newValue: string,
    oldValue: string
  ) {
    setUpdatingIds((prev) => new Set(prev).add(itemId));
    // Optimistic update
    setConfigs((prev) =>
      prev.map((c) =>
        c.id === productId
          ? {
              ...c,
              items: c.items.map((i) => (i.id === itemId ? { ...i, [field]: newValue } : i)),
            }
          : c
      )
    );
    try {
      const result = await missionControlService.updateProductIntelItem(itemId, {
        [field]: newValue,
      });
      if (!result.success) {
        setConfigs((prev) =>
          prev.map((c) =>
            c.id === productId
              ? {
                  ...c,
                  items: c.items.map((i) => (i.id === itemId ? { ...i, [field]: oldValue } : i)),
                }
              : c
          )
        );
      }
    } catch {
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === productId
            ? {
                ...c,
                items: c.items.map((i) => (i.id === itemId ? { ...i, [field]: oldValue } : i)),
              }
            : c
        )
      );
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }

  async function handleAddItem(productId: string) {
    const product = configs.find((c) => c.id === productId);
    const sortOrder = product ? product.items.length : 0;
    try {
      const result = await missionControlService.addProductIntelItem(productId, {
        label: 'New Field',
        value: '—',
        sortOrder,
      });
      if (result.success && result.data) {
        setConfigs((prev) =>
          prev.map((c) => (c.id === productId ? { ...c, items: [...c.items, result.data!] } : c))
        );
      }
    } catch {
      setError('Failed to add item.');
    }
  }

  async function handleDeleteItem(itemId: string, productId: string) {
    const product = configs.find((c) => c.id === productId);
    const oldItems = product?.items ?? [];
    // Optimistic removal
    setConfigs((prev) =>
      prev.map((c) =>
        c.id === productId ? { ...c, items: c.items.filter((i) => i.id !== itemId) } : c
      )
    );
    try {
      const result = await missionControlService.deleteProductIntelItem(itemId);
      if (!result.success) {
        setConfigs((prev) => prev.map((c) => (c.id === productId ? { ...c, items: oldItems } : c)));
      }
    } catch {
      setConfigs((prev) => prev.map((c) => (c.id === productId ? { ...c, items: oldItems } : c)));
    }
  }

  return (
    <>
      <div
        className={`mc-settings-backdrop${isOpen ? ' mc-settings-backdrop--open' : ''}`}
        onClick={onClose}
        data-testid="pi-settings-backdrop"
      />
      <div
        className={`mc-settings-drawer${isOpen ? ' mc-settings-drawer--open' : ''}`}
        role="dialog"
        aria-label="Product Intel Settings"
      >
        <div className="mc-settings__glow" />
        <div className="mc-settings__header">
          <div className="mc-settings__title-group">
            <div className="mc-settings__title-bar" />
            <h3 className="mc-settings__title">Product Intel Settings</h3>
          </div>
          <button
            className="mc-settings__close-btn"
            onClick={onClose}
            aria-label="Close product intel settings"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {error && <div className="mc-settings__error">{error}</div>}

        <div className="mc-settings__body">
          {loading ? (
            <div className="mc-settings__loading">Loading products...</div>
          ) : configs.length === 0 ? (
            <div className="mc-settings__empty">No product configurations found.</div>
          ) : (
            configs.map((product) => (
              <ProductGroup
                key={product.id}
                config={product}
                updatingIds={updatingIds}
                onUpdateProduct={handleUpdateProduct}
                onUpdateItem={handleUpdateItem}
                onAddItem={handleAddItem}
                onDeleteItem={handleDeleteItem}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

// ── Product Group ──────────────────────────────────────

interface ProductGroupProps {
  config: ProductConfig;
  updatingIds: Set<string>;
  onUpdateProduct: (
    id: string,
    field: 'name' | 'platform',
    value: string,
    oldValue: string
  ) => void;
  onUpdateItem: (
    itemId: string,
    productId: string,
    field: 'label' | 'value',
    newValue: string,
    oldValue: string
  ) => void;
  onAddItem: (productId: string) => void;
  onDeleteItem: (itemId: string, productId: string) => void;
}

function ProductGroup({
  config,
  updatingIds,
  onUpdateProduct,
  onUpdateItem,
  onAddItem,
  onDeleteItem,
}: ProductGroupProps): React.ReactElement {
  const [localName, setLocalName] = useState(config.name);

  useEffect(() => {
    setLocalName(config.name);
  }, [config.name]);

  function commitName() {
    const trimmed = localName.trim();
    if (trimmed && trimmed !== config.name) {
      onUpdateProduct(config.id, 'name', trimmed, config.name);
    } else {
      setLocalName(config.name);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  }

  const updating = updatingIds.has(config.id);

  return (
    <div className="mc-pi-group">
      <div className="mc-pi-group__header">
        <input
          className="mc-pi-group__name-input"
          type="text"
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
          onBlur={commitName}
          onKeyDown={handleKeyDown}
          disabled={updating}
          aria-label={`Product name for ${config.name}`}
        />
        <div
          className="mc-pi-platform-selector"
          role="radiogroup"
          aria-label={`Platform for ${config.name}`}
        >
          {PLATFORMS.map((p) => (
            <button
              key={p}
              className={`mc-pi-platform-selector__btn mc-pi-platform-selector__btn--${PLATFORM_CSS[p]}${config.platform === p ? ' mc-pi-platform-selector__btn--active' : ''}`}
              role="radio"
              aria-checked={config.platform === p}
              disabled={updating}
              onClick={() => {
                if (config.platform !== p) {
                  onUpdateProduct(config.id, 'platform', p, config.platform);
                }
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {config.items.map((item) => (
        <ProductItemRow
          key={item.id}
          item={item}
          productId={config.id}
          updating={updatingIds.has(item.id)}
          onUpdate={onUpdateItem}
          onDelete={onDeleteItem}
        />
      ))}

      <button
        className="mc-pi-add-item-btn"
        onClick={() => onAddItem(config.id)}
        aria-label={`Add item to ${config.name}`}
      >
        + ADD FIELD
      </button>
    </div>
  );
}

// ── Product Item Row ───────────────────────────────────

interface ProductItemRowProps {
  item: ProductIntelItemConfig;
  productId: string;
  updating: boolean;
  onUpdate: (
    itemId: string,
    productId: string,
    field: 'label' | 'value',
    newValue: string,
    oldValue: string
  ) => void;
  onDelete: (itemId: string, productId: string) => void;
}

function ProductItemRow({
  item,
  productId,
  updating,
  onUpdate,
  onDelete,
}: ProductItemRowProps): React.ReactElement {
  const [localLabel, setLocalLabel] = useState(item.label);
  const [localValue, setLocalValue] = useState(item.value);

  useEffect(() => {
    setLocalLabel(item.label);
    setLocalValue(item.value);
  }, [item.label, item.value]);

  function commitLabel() {
    const trimmed = localLabel.trim();
    if (trimmed && trimmed !== item.label) {
      onUpdate(item.id, productId, 'label', trimmed, item.label);
    } else {
      setLocalLabel(item.label);
    }
  }

  function commitValue() {
    const trimmed = localValue.trim();
    if (trimmed !== item.value) {
      onUpdate(item.id, productId, 'value', trimmed, item.value);
    } else {
      setLocalValue(item.value);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  }

  return (
    <div className="mc-pi-item-row">
      <div className="mc-pi-item-row__field mc-pi-item-row__field--label">
        <span className="mc-pi-item-row__field-label">LABEL</span>
        <input
          className="mc-pi-item-row__input"
          type="text"
          value={localLabel}
          onChange={(e) => setLocalLabel(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={handleKeyDown}
          disabled={updating}
          aria-label={`Label for ${item.label}`}
        />
      </div>
      <div className="mc-pi-item-row__field mc-pi-item-row__field--value">
        <span className="mc-pi-item-row__field-label">VALUE</span>
        <input
          className="mc-pi-item-row__input"
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={commitValue}
          onKeyDown={handleKeyDown}
          disabled={updating}
          aria-label={`Value for ${item.label}`}
        />
      </div>
      <button
        className="mc-pi-item-row__delete-btn"
        onClick={() => onDelete(item.id, productId)}
        aria-label={`Delete ${item.label}`}
        title="Remove field"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 2l8 8M10 2l-8 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}
